import { getFormProps, getInputProps, useForm } from "@conform-to/react";
import { getZodConstraint, parseWithZod } from "@conform-to/zod";
import { Form } from "@remix-run/react";
import { Button, Heading, LogoBlueOnWhite } from "@repo/ui";
import { z } from "zod";
import logoBlueOnWhite from "~/assets/brand/logoBlueOnWhite.svg";

export type FormFields = z.infer<typeof BookDemoSchema>;

export const BookDemoSchema = z.object({
  name: z.string().min(1, { message: "Please enter your name" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  organisationName: z
    .string()
    .min(1, { message: "Please enter your organisation name" }),
  website: z.string().min(1, { message: "Please enter your website" }),
  referralCode: z.string().optional(),
});

export function SignUpPage() {
  const [form, fields] = useForm({
    constraint: getZodConstraint(BookDemoSchema),
    shouldValidate: "onBlur",
    shouldRevalidate: "onInput",
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: BookDemoSchema });
    },
  });

  return (
    <div className="flex h-full items-center justify-center bg-gradient-to-t bg-zinc-50 from-zinc-200 to-zinc-50 font-sans">
      <div className="flex min-w-96 max-w-screen-md flex-col gap-6 rounded-md bg-white p-8 text-zinc-700 shadow-md">
        <LogoBlueOnWhite src={logoBlueOnWhite} />
        <Heading as="h1" size="lg">
          Book a demo
        </Heading>

        <Form
          method="post"
          {...getFormProps(form)}
          className="flex flex-col gap-6"
        >
          {/* Name */}
          <div className="group flex flex-col items-start gap-1 text-sm">
            <label
              htmlFor={fields.name.id}
              className="whitespace-nowrap font-mona text-zinc-500"
            >
              Your full name
            </label>
            <input
              className="w-full rounded-md border border-zinc-300 p-2"
              {...getInputProps(fields.name, { type: "text" })}
            />
          </div>

          {/* Email */}
          <div className="group flex flex-col items-start gap-1 text-sm">
            <label
              htmlFor={fields.email.id}
              className="whitespace-nowrap font-mona text-zinc-500"
            >
              Your email address
            </label>
            <input
              className="w-full rounded-md border border-zinc-300 p-2"
              {...getInputProps(fields.email, { type: "email" })}
            />
          </div>

          {/* Organisation */}
          <div className="group flex flex-col items-start gap-1 text-sm">
            <label
              htmlFor={fields.organisationName.id}
              className="whitespace-nowrap font-mona text-zinc-500"
            >
              Name of your organisation
            </label>
            <input
              className="w-full rounded-md border border-zinc-300 p-2"
              {...getInputProps(fields.organisationName, { type: "text" })}
            />
          </div>

          {/* Website */}
          <div className="group flex flex-col items-start gap-1 text-sm">
            <label
              htmlFor={fields.website.id}
              className="whitespace-nowrap font-mona text-zinc-500"
            >
              Organisation's website
            </label>
            <input
              className="w-full rounded-md border border-zinc-300 p-2"
              {...getInputProps(fields.website, { type: "url" })}
            />
          </div>

          {/* Referral code */}
          <div className="group flex flex-col items-start gap-1 text-sm">
            <label
              htmlFor={fields.referralCode.id}
              className="whitespace-nowrap font-mona text-zinc-500"
            >
              Referral code (optional)
            </label>
            <input
              className="w-full rounded-md border border-zinc-300 p-2"
              {...getInputProps(fields.referralCode, { type: "text" })}
            />
          </div>

          <Button size="lg" type="submit">
            Confirm
          </Button>
        </Form>

        <div className="flex flex-col gap-4 pt-8 font-sans text-sm text-zinc-500">
          <div>
            Already have an account?{" "}
            <a href="/sign-in" className="text-brand-700 hover:underline">
              Sign in
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
